import { getToken } from '../../../../../utils/auth';

/**
 * Per-user localStorage for the contractor workspace.
 *
 * Every workspace key starts with `cw_` and is suffixed with the signed-in user's
 * id, so two people sharing a browser (a site office laptop, a van tablet) never
 * see each other's cached company profile, terminology or drafts. Every call is
 * wrapped in try/catch: storage can be disabled, full, or throw in private mode.
 *
 *   storageGet('company_profile', null)   → parsed JSON or the fallback
 *   storageSet('company_profile', obj)
 *   storageRemove('company_profile')
 *   scopedKey('company_profile')          → 'cw_company_profile__u42'
 *   clearWorkspaceStorage()               → removes every cw_* key (logout)
 */

const PREFIX = 'cw_';

/** The signed-in user's id, read from the JWT payload (`userId`, falling back to `id`/`sub`). */
export function currentUserId() {
  try {
    const token = getToken();
    if (!token) return null;
    const part = String(token).split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const id = json?.userId ?? json?.id ?? json?.sub;
    return id == null ? null : String(id);
  } catch {
    return null;
  }
}

/** Namespaced storage key for the current user. Accepts 'foo' or 'cw_foo'. */
export function scopedKey(key, userId = currentUserId()) {
  const base = String(key).startsWith(PREFIX) ? String(key) : `${PREFIX}${key}`;
  return `${base}__u${userId || 'anon'}`;
}

/** Read and JSON-parse a per-user value. Returns `fallback` when missing or unreadable. */
export function storageGet(key, fallback = null) {
  try {
    const raw = window.localStorage.getItem(scopedKey(key));
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/** JSON-serialise and store a per-user value. Returns true on success. */
export function storageSet(key, value) {
  try {
    window.localStorage.setItem(scopedKey(key), JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function storageRemove(key) {
  try { window.localStorage.removeItem(scopedKey(key)); } catch { /* storage unavailable */ }
}

/**
 * Remove every `cw_*` key from localStorage and sessionStorage — namespaced and
 * legacy un-namespaced alike. Call on logout so the next person on this device
 * starts clean.
 */
export function clearWorkspaceStorage() {
  [() => window.localStorage, () => window.sessionStorage].forEach((getStore) => {
    try {
      const store = getStore();
      const doomed = [];
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (k && k.startsWith(PREFIX)) doomed.push(k);
      }
      doomed.forEach((k) => store.removeItem(k));
    } catch { /* storage unavailable */ }
  });
}

export default storageGet;
